'use client';
import { FaHandshake } from 'react-icons/fa';
import useClickOutside from '@/hooks/useClickOutside';
import useLinks from '@/hooks/useLinks';
import { SupportedLanguages, useTranslation } from '@/hooks/useTranslation';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BiArrowBack,
  BiCaretDown,
  BiCaretRight,
  BiCheck,
  BiDollarCircle,
  BiMenu,
  BiMoviePlay,
  BiRightArrowAlt,
  BiSolidConversation,
  BiSolidHome,
  BiSolidPhone,
  BiSolidZap,
  BiX,
} from 'react-icons/bi';
import { FaShop } from 'react-icons/fa6';
import { RiSparkling2Line } from 'react-icons/ri';
import { HiOutlineCpuChip } from 'react-icons/hi2';
import { LiaCloneSolid } from 'react-icons/lia';
import LanguageChangeDropdown, { LanguageMapper } from '../common/LanguageChangeDropdown';
import OfferBanner from '../blocks/OfferBanner';
import { lngFlagMapper } from '@/components/common/LanguageChangeDropdown';
import { locales } from '@/libs/i18n';
import { AiOutlineGlobal } from 'react-icons/ai';

type Props = { locale: string };

type FeatureProps = {
  feature: {
    menu: Array<{
      title: string;
      description: string;
      icon: React.ReactNode;
      link: string;
      badge?: string;
    }>;
    blogs: Array<{
      title: string;
      description: string;
      imageUrl: string;
      link: string;
    }>;
  };
  businessPartner: {
    menu: Array<{
      title: string;
      description: string;
      icon: React.ReactNode;
      link: string;
    }>;
  };
};

export const NAVIGATION_CONSTANTS = {
  SCROLL_THRESHOLD: 10,
  Z_INDEX: 50,
  MAX_Z_INDEX: 9999999,
};

export default function SiteNavbarComponent({ locale }: Props) {
  const pathname = usePathname();
  const { t } = useTranslation(locale);
  const { Links } = useLinks(locale as SupportedLanguages);

  const feature = useMemo(() => {
    return {
      menu: [
        {
          title: t('HeaderMenu/features/aiChatbots/title'),
          description: t('HeaderMenu/features/aiChatbots/description'),
          icon: <HiOutlineCpuChip className="tw-w-6 tw-h-6 tw-shrink-0 tw-text-blue-400" />,
          link: Links.AIChatbot.href,
          badge: t('HeaderMenu/features/aiChatbots/badge'),
        },
        {
          title: t('HeaderMenu/features/chatbotsWithFlow/title'),
          description: t('HeaderMenu/features/chatbotsWithFlow/description'),
          icon: <BiSolidConversation className="tw-w-6 tw-h-6 tw-shrink-0 tw-text-green-400" />,
          link: Links.ScenarioChatbot.href,
        },
        {
          title: t('HeaderMenu/features/videoPopUp/title'),
          description: t('HeaderMenu/features/videoPopUp/description'),
          icon: <BiMoviePlay className="tw-w-6 tw-h-6 tw-shrink-0 tw-text-purple-400" />,
          link: Links.VideoBot.href,
        },
        {
          title: t('HeaderMenu/features/callBot/title'),
          description: t('HeaderMenu/features/callBot/description'),
          icon: <BiSolidPhone className="tw-w-6 tw-h-6 tw-shrink-0 tw-text-yellow-500" />,
          link: Links.CallBotPage.href,
          badge: t('HeaderMenu/features/callBot/badge'),
        },
        {
          title: t('HeaderMenu/features/siteBot/title'),
          description: t('HeaderMenu/features/siteBot/description'),
          icon: <AiOutlineGlobal className="tw-w-6 tw-h-6 tw-shrink-0 tw-text-purple-400" />,
          link: Links.SiteBot.href,
        },
        {
          title: t('HeaderMenu/features/aiClone/title'),
          description: t('HeaderMenu/features/aiClone/description'),
          icon: <LiaCloneSolid className={'tw-w-6 tw-h-6 tw-shrink-0 tw-text-orange-400'} />,
          link: Links.HumanClone.href,
          badge: t('HeaderMenu/features/aiClone/badge'),
        },
      ],
      blogs: [
        {
          title: t('HeaderMenu/blog/posts/post3/title'),
          description: t('HeaderMenu/blog/posts/post3/description'),
          imageUrl: 'https://cdn.1cdn.app/application/LOUISEBOT/25120621515616_marketplace_louisebot.png',
          link: Links.Marketplace.href,
        },
        {
          title: t('HeaderMenu/blog/posts/post1/title'),
          description: t('HeaderMenu/blog/posts/post1/description'),
          imageUrl: 'https://cdn.1cdn.app/application/LOUISEBOT/25080908382190_voicemail_img.png',
          link: Links.Voicemail.href,
        },
      ],
    };
  }, [Links, t]);

  const businessParnet = useMemo(() => {
    return {
      menu: [
        {
          title: t('HeaderMenu/businessPartner/Agencies/title'),
          description: t('HeaderMenu/businessPartner/Agencies/description'),
          icon: <FaShop className="tw-w-6 tw-h-6 tw-shrink-0 tw-text-blue-400" />,
          link: Links.partnership.href,
        },
        {
          title: t('HeaderMenu/businessPartner/AffiliateProgram/title'),
          description: t('HeaderMenu/businessPartner/AffiliateProgram/description'),
          icon: <BiDollarCircle className="tw-w-6 tw-h-6 tw-shrink-0 tw-text-green-400" />,
          link: Links.BecomeAnAffiliate.href,
        },
      ],
    };
  }, [Links, t]);

  const [show, setShow] = useState(true);

  if (pathname?.includes('chat/')) return null;

  return (
    <React.Fragment>
      {/* Offer Banner */}
      {show && <OfferBanner setShow={setShow} links={Links} locale={locale} />}
      {/* Desktop or Mobile Menu */}
      <DesktopMenu feature={feature} businessPartner={businessParnet} locale={locale} />
      <MobileMenu feature={feature} businessPartner={businessParnet} locale={locale} />
    </React.Fragment>
  );
}

const MobileMenu = ({
  feature,
  businessPartner,
  locale,
}: {
  feature: FeatureProps['feature'];
  businessPartner: FeatureProps['businessPartner'];
  locale: Props['locale'];
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [currentMenu, setCurrentMenu] = useState('main');
  const pathname = usePathname();
  const router = useRouter();
  const { Links } = useLinks(locale as SupportedLanguages);
  const { t } = useTranslation(locale);
  const ref = useRef(null);

  useClickOutside(ref, () => {
    setShowMenu(false);
    setCurrentMenu('main');
  });

  const selectHandler = (newLocale: string) => {
    if (!pathname) return `/${newLocale}`;
    const segments = pathname.split('/');
    if (locales.includes(segments[1] as any)) {
      segments[1] = newLocale;
    } else {
      segments.splice(1, 0, newLocale);
    }
    return segments.join('/');
  };

  const handleClick = (selectionHandler: () => string) => {
    const newPath = selectionHandler();
    if (newPath) {
      router.push(newPath);
    }
    setShowMenu(false);
    setCurrentMenu('main');
  };

  const toggleMenu = () => {
    setCurrentMenu('main');
    setShowMenu(!showMenu);
  };

  const goToMainMenu = () => setCurrentMenu('main');
  const goToFeaturesMenu = () => setCurrentMenu('features');
  const goToBusinessPartnerMenu = () => setCurrentMenu('businessPartner');
  const goToLanguageMenu = () => setCurrentMenu('language');

  const displayLanguageName = () => {
    return LanguageMapper[locale] || LanguageMapper['en-US'];
  };

  return (
    <div
      ref={ref}
      style={{ zIndex: showMenu ? NAVIGATION_CONSTANTS.MAX_Z_INDEX : NAVIGATION_CONSTANTS.Z_INDEX }}
      className={`${
        showMenu ? 'tw-fixed tw-inset-0 tw-bg-gray-900 tw-h-screen tw-flex-col' : 'tw-sticky tw-top-0'
      } lg:tw-hidden tw-flex tw-w-full tw-font-['Lexend']`}
    >
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-bg-gray-900 tw-w-full tw-border-b tw-border-gray-700">
        <Link className="tw-flex tw-items-center tw-gap-2" passHref href={Links.home.href}>
          <Image
            src="https://cdn.1cdn.app/application/LOUISEBOT/25072015190193_logo_fond_blanc.png"
            alt="LouiseBot logo"
            className="tw-w-8 tw-h-8 tw-rounded-full"
            width={40}
            height={40}
            priority
          />
          <span className="tw-text-gray-300 tw-text-lg tw-font-extrabold">LouiseBot</span>
        </Link>
        <button
          onClick={toggleMenu}
          className="tw-p-2 tw-rounded-xl tw-bg-gray-800 hover:tw-bg-gray-700 tw-transition-colors tw-duration-200"
        >
          {showMenu ? (
            <BiX size={24} className="tw-text-gray-300" />
          ) : (
            <BiMenu size={24} className="tw-text-gray-300" />
          )}
        </button>
      </div>

      {/* Dropdown Menu */}
      {showMenu && (
        <div className="tw-bg-gray-900 tw-w-full tw-h-screen tw-flex tw-flex-col tw-px-4 tw-overflow-y-auto">
          {/* Main Menu */}
          {currentMenu === 'main' && (
            <>
              <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-6">
                {/* Home Link */}
                <Link
                  passHref
                  href={Links.home.href}
                  className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-bg-gray-800"
                  onClick={() => setShowMenu(false)}
                >
                  <BiSolidHome className="tw-w-6 tw-h-6 tw-text-blue-400" />
                  <span className="tw-text-gray-100 tw-font-medium">{t('SiteNavbar/l-05')}</span>
                </Link>

                {/* Features Menu */}
                <button
                  onClick={goToFeaturesMenu}
                  className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-bg-gray-800 tw-bg-transparent tw-border-0"
                >
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <BiSolidZap className="tw-w-6 tw-h-6 tw-text-purple-400" />
                    <span className="tw-text-gray-100 tw-font-medium">{t('SiteNavbar/l-10')}</span>
                    <span className="tw-bg-blue-500/20 tw-text-blue-400 tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded-full tw-border tw-border-blue-500/30">
                      {t('SiteNavbar/new')}
                    </span>
                  </div>
                  <BiCaretRight className="tw-w-5 tw-h-5 tw-text-gray-400" />
                </button>

                {/* Pricing Link */}
                <Link
                  passHref
                  href={Links.pricing.href}
                  className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-bg-gray-800"
                  onClick={() => setShowMenu(false)}
                >
                  <BiDollarCircle className="tw-w-6 tw-h-6 tw-text-green-400" />
                  <span className="tw-text-gray-100 tw-font-medium">{t('SiteNavbar/l-03')}</span>
                </Link>

                {/* Business Partners */}
                <button
                  onClick={goToBusinessPartnerMenu}
                  className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-bg-gray-800 tw-bg-transparent tw-border-0"
                >
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <FaHandshake className="tw-w-6 tw-h-6 tw-text-orange-400" />
                    <span className="tw-text-gray-100 tw-font-medium">{t('SiteNavbar/l-15')}</span>
                  </div>
                  <BiCaretRight className="tw-w-5 tw-h-5 tw-text-gray-400" />
                </button>

                {/* Marketplace Link */}
                <Link
                  passHref
                  href={Links.Marketplace.href}
                  className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-bg-gray-800"
                  onClick={() => setShowMenu(false)}
                >
                  <FaShop className="tw-w-6 tw-h-6 tw-text-pink-400" />
                  <span className="tw-text-gray-100 tw-font-medium">{t('HeaderMenu/blog/posts/post3/title')}</span>
                  <span className="tw-bg-blue-500/20 tw-text-blue-400 tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded-full tw-border tw-border-blue-500/30">
                    {t('SiteNavbar/new')}
                  </span>
                </Link>

                {/* Language Selection */}
                <button
                  onClick={goToLanguageMenu}
                  className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-bg-gray-800 tw-bg-transparent tw-border-0"
                >
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <Image
                      priority
                      quality={100}
                      src={lngFlagMapper[locale]}
                      alt={locale}
                      width={28}
                      height={28}
                      className="tw-rounded-full tw-border tw-border-gray-600"
                    />
                    <span className="tw-text-gray-100 tw-font-medium">{displayLanguageName()}</span>
                  </div>
                  <BiCaretRight className="tw-w-5 tw-h-5 tw-text-gray-400" />
                </button>
              </div>

              {/* CTA Buttons */}
              <div className="tw-flex tw-flex-col tw-gap-3 tw-mt-8 tw-pt-6 tw-border-t tw-border-gray-700">
                <Link
                  passHref
                  href={Links.login.href}
                  className="tw-w-full tw-px-6 tw-py-3 tw-bg-gray-800 hover:tw-bg-gray-700 tw-rounded-xl tw-text-gray-100 tw-font-semibold tw-text-center tw-transition-colors tw-duration-200 tw-border tw-border-gray-700"
                  onClick={() => setShowMenu(false)}
                >
                  {Links.login.name}
                </Link>
                <Link
                  passHref
                  href={Links.register.href}
                  className="tw-w-full tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 hover:tw-from-blue-700 hover:tw-to-purple-700 tw-text-white tw-font-semibold tw-rounded-xl tw-transition-all tw-duration-200 tw-shadow-lg hover:tw-shadow-xl tw-transform hover:tw-scale-105 tw-flex tw-justify-center tw-items-center tw-gap-2"
                  onClick={() => setShowMenu(false)}
                >
                  {Links.register.name}
                  <Image
                    priority
                    quality={100}
                    src="https://cdn.1cdn.app/application/LOUISEBOT/25072220485283_logo_google.png"
                    alt={locale}
                    width={24}
                    height={24}
                    className="tw-rounded-full tw-border tw-border-gray-600 tw-shrink-0"
                  />
                </Link>
              </div>
            </>
          )}

          {/* Features Submenu */}
          {currentMenu === 'features' && (
            <div className="tw-py-6">
              <button
                onClick={goToMainMenu}
                className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-text-gray-400 tw-font-medium tw-bg-transparent tw-border-0"
              >
                <BiArrowBack className="tw-w-5 tw-h-5" />
                {t('HeaderMenu/BacktoMainMenu')}
              </button>

              <div className="tw-space-y-4 tw-mt-6">
                {feature.menu.map((item, index) => (
                  <Link
                    passHref
                    href={item.link}
                    key={index}
                    className="tw-flex tw-items-start tw-gap-4 tw-p-4 tw-rounded-xl tw-border tw-border-gray-700 hover:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-bg-gray-800"
                    onClick={() => setShowMenu(false)}
                  >
                    {item.icon}
                    <div className="tw-flex-grow">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                        <h4 className="tw-text-gray-100 tw-font-semibold">{item.title}</h4>
                        {item.badge && (
                          <span className="tw-bg-green-500/20 tw-text-green-400 tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded-full tw-border tw-border-green-500/30">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <p className="tw-text-gray-400 tw-text-sm tw-leading-relaxed">{item.description}</p>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Blog Section */}
              <div className="tw-mt-8 tw-pt-6 tw-border-t tw-border-gray-700">
                <h3 className="tw-text-gray-400 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wider tw-mb-4">
                  {t('SiteNavbar/l-16')}
                </h3>
                <div className="tw-space-y-4">
                  {feature.blogs.map((item, index) => (
                    <Link
                      passHref
                      key={index}
                      href={item.link}
                      className="tw-flex tw-gap-4 tw-p-4 tw-rounded-xl tw-border tw-border-gray-700 hover:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-bg-gray-800"
                      onClick={() => setShowMenu(false)}
                    >
                      <Image
                        priority
                        quality={100}
                        src={item.imageUrl}
                        alt={item.title}
                        width={80}
                        height={60}
                        className="tw-rounded-lg tw-object-cover tw-flex-shrink-0"
                      />
                      <div className="tw-flex-grow tw-min-w-0">
                        <h4 className="tw-text-gray-100 tw-font-semibold tw-text-sm tw-mb-1 tw-line-clamp-2">
                          {item.title}
                        </h4>
                        <p className="tw-text-gray-400 tw-text-xs tw-line-clamp-2">{item.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Business Partner Submenu */}
          {currentMenu === 'businessPartner' && (
            <div className="tw-py-6">
              <button
                onClick={goToMainMenu}
                className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-text-gray-400 tw-font-medium tw-bg-transparent tw-border-0"
              >
                <BiArrowBack className="tw-w-5 tw-h-5" />
                {t('HeaderMenu/BacktoMainMenu')}
              </button>

              <div className="tw-space-y-4 tw-mt-6">
                {businessPartner.menu.map((item, index) => (
                  <Link
                    passHref
                    href={item.link}
                    key={index}
                    className="tw-flex tw-items-start tw-gap-4 tw-p-4 tw-rounded-xl tw-border tw-border-gray-700 hover:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-bg-gray-800"
                    onClick={() => setShowMenu(false)}
                  >
                    {item.icon}
                    <div>
                      <h4 className="tw-text-gray-100 tw-font-semibold tw-mb-1">{item.title}</h4>
                      <p className="tw-text-gray-400 tw-text-sm tw-leading-relaxed">{item.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Language Submenu */}
          {currentMenu === 'language' && (
            <div className="tw-py-6">
              <button
                onClick={goToMainMenu}
                className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-text-gray-400 tw-font-medium tw-bg-transparent tw-border-0"
              >
                <BiArrowBack className="tw-w-5 tw-h-5" />
                {t('HeaderMenu/BacktoMainMenu')}
              </button>

              <div className="tw-space-y-3 tw-mt-6">
                <button
                  onClick={() => handleClick(() => selectHandler('en-US'))}
                  className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-4 tw-rounded-xl tw-border tw-border-gray-700 hover:tw-border-blue-500 tw-transition-all tw-duration-200 tw-bg-transparent hover:tw-bg-gray-800"
                >
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <Image
                      priority
                      quality={100}
                      src={lngFlagMapper['en-US']}
                      alt="English"
                      width={28}
                      height={28}
                      className="tw-rounded-full tw-border tw-border-gray-600"
                    />
                    <span className="tw-text-gray-100 tw-font-medium">English</span>
                  </div>
                  {locale === 'en-US' && <BiCheck className="tw-w-6 tw-h-6 tw-text-blue-400" />}
                </button>

                <button
                  onClick={() => handleClick(() => selectHandler('fr-FR'))}
                  className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-4 tw-rounded-xl tw-border tw-border-gray-700 hover:tw-border-blue-500 tw-transition-all tw-duration-200 tw-bg-transparent hover:tw-bg-gray-800"
                >
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <Image
                      priority
                      quality={100}
                      src={lngFlagMapper['fr-FR']}
                      alt="French"
                      width={28}
                      height={28}
                      className="tw-rounded-full tw-border tw-border-gray-600"
                    />
                    <span className="tw-text-gray-100 tw-font-medium">Français</span>
                  </div>
                  {locale === 'fr-FR' && <BiCheck className="tw-w-6 tw-h-6 tw-text-blue-400" />}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DesktopMenu = ({
  feature,
  businessPartner,
  locale,
}: {
  feature: FeatureProps['feature'];
  businessPartner: FeatureProps['businessPartner'];
  locale: Props['locale'];
}) => {
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation(locale);
  const { Links } = useLinks(locale as SupportedLanguages);
  const [showFeatures, setShowFeatures] = useState(false);
  const [showBusinessPartner, setShowBusinessPartner] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useClickOutside(ref, () => {
    onClose();
  });

  // Prevent background/body scroll when dropdown is open
  useEffect(() => {
    if (showFeatures || showBusinessPartner) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showFeatures, showBusinessPartner]);

  const onClose = () => {
    setShowFeatures(false);
    setShowBusinessPartner(false);
  };

  return (
    <div
      ref={ref}
      style={{ zIndex: NAVIGATION_CONSTANTS.Z_INDEX }}
      className={`${
        scrolled ? 'tw-sticky tw-top-0 tw-shadow-lg tw-backdrop-blur-sm tw-bg-gray-900/95' : 'tw-bg-gray-900'
      } tw-w-full tw-transition-all tw-duration-300 tw-border-b tw-border-gray-700 tw-relative lg:tw-flex tw-hidden tw-font-['Lexend']`}
    >
      <div className="tw-container tw-mx-auto tw-px-6 tw-py-4">
        <div className="tw-flex tw-items-center tw-justify-between">
          {/* Logo & Navigation */}
          <div className="tw-flex tw-items-center tw-gap-8">
            <Link passHref href={Links.home.href} className="tw-flex tw-items-center tw-gap-2">
              <Image
                priority
                quality={100}
                src="https://cdn.1cdn.app/application/LOUISEBOT/25072015190193_logo_fond_blanc.png"
                alt="LouiseBot logo"
                className="tw-w-8 tw-h-8 tw-rounded-full"
                width={40}
                height={40}
              />
              <span className="tw-text-gray-300 tw-text-xl tw-font-extrabold">LouiseBot</span>
            </Link>

            <nav className="tw-flex tw-items-center tw-gap-8 tw-mx-8">
              <div className="tw-relative">
                <button
                  onClick={() => {
                    setShowBusinessPartner(false);
                    setShowFeatures((prev) => !prev);
                  }}
                  className="tw-flex tw-items-center tw-gap-1 tw-text-gray-300 hover:tw-text-blue-400 tw-font-medium tw-transition-colors tw-duration-200 tw-bg-transparent tw-border-0 tw-p-0"
                >
                  {t('SiteNavbar/l-10')}
                  <span className="tw-bg-gradient-to-r tw-from-blue-500/20 tw-to-purple-500/20 tw-text-blue-400 tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded-full tw-ml-2 tw-border tw-border-blue-500/30">
                    {t('SiteNavbar/new')}
                  </span>
                  <BiCaretDown
                    className={`tw-w-4 tw-h-4 tw-transition-transform tw-duration-200 ${
                      showFeatures ? 'tw-rotate-180' : ''
                    }`}
                  />
                </button>
              </div>
              <div className="tw-relative">
                <button
                  onClick={() => {
                    setShowFeatures(false);
                    setShowBusinessPartner((prev) => !prev);
                  }}
                  className="tw-flex tw-items-center tw-gap-1 tw-text-gray-300 hover:tw-text-blue-400 tw-font-medium tw-transition-colors tw-duration-200 tw-bg-transparent tw-border-0 tw-p-0"
                >
                  {t('SiteNavbar/l-15')}
                  <BiCaretDown
                    className={`tw-w-4 tw-h-4 tw-transition-transform tw-duration-200 ${
                      showBusinessPartner ? 'tw-rotate-180' : ''
                    }`}
                  />
                </button>
              </div>
              <Link
                passHref
                onClick={onClose}
                href={Links.pricing.href}
                className="tw-text-gray-300 hover:tw-text-blue-400 tw-font-medium tw-transition-colors tw-duration-200"
              >
                {Links.pricing.name}
              </Link>
            </nav>
          </div>

          {/* Right Actions */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <LanguageChangeDropdown id="language-dropdown-navbar" locale={locale as SupportedLanguages} />

            <Link
              passHref
              onClick={onClose}
              href={Links.login.href}
              className="tw-px-4 tw-py-2 tw-text-gray-300 hover:tw-text-blue-400 tw-font-medium tw-transition-colors tw-duration-200"
            >
              {Links.login.name}
            </Link>
            <Link
              passHref
              onClick={onClose}
              href={Links.register.href}
              className="tw-px-4 tw-py-2 tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 hover:tw-from-blue-700 hover:tw-to-purple-700 tw-text-white tw-font-semibold tw-rounded-xl tw-transition-all tw-duration-200 tw-shadow-lg hover:tw-shadow-xl tw-transform hover:tw-scale-105 tw-flex tw-items-center tw-gap-2"
            >
              {Links.register.name}
              <Image
                priority
                quality={100}
                src="https://cdn.1cdn.app/application/LOUISEBOT/25072220485283_logo_google.png"
                alt={locale}
                width={24}
                height={24}
                className="tw-rounded-full tw-border tw-border-gray-600 tw-shrink-0"
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Features Dropdown */}
      {showFeatures && (
        <div className="tw-absolute tw-top-full tw-left-0 tw-w-full tw-bg-gray-800 tw-shadow-2xl tw-border-t tw-border-gray-700 tw-z-50 tw-max-h-[100vh] tw-overflow-auto">
          <div className="tw-container tw-mx-auto tw-px-6 tw-py-8 tw-max-h-[80vh] ">
            <div className="tw-grid tw-grid-cols-2 tw-gap-12">
              {/* Features List */}
              <div className="tw-space-y-6">
                <h3 className="tw-text-gray-100 tw-font-bold tw-text-lg tw-flex tw-items-center tw-gap-2">
                  <RiSparkling2Line className="tw-w-5 tw-h-5 tw-text-blue-400" />
                  {t('HeaderMenu/features/title')}
                </h3>
                <div className="tw-space-y-4">
                  {feature.menu.map((item, index) => (
                    <Link
                      passHref
                      href={item.link}
                      key={index}
                      className="tw-group tw-flex tw-items-start tw-gap-4 tw-p-4 tw-rounded-xl tw-border tw-border-gray-700 hover:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-bg-gray-700"
                      onClick={onClose}
                    >
                      <div className="tw-flex-shrink-0 tw-p-2 tw-bg-gray-700 tw-rounded-lg group-hover:tw-bg-gray-600 tw-transition-colors tw-duration-200">
                        {item.icon}
                      </div>
                      <div className="tw-flex-grow">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                          <h4 className="tw-text-gray-100 tw-font-semibold group-hover:tw-text-blue-400 tw-transition-colors tw-duration-200">
                            {item.title}
                          </h4>
                          {item.badge && (
                            <span className="tw-bg-green-500/20 tw-text-green-400 tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded-full tw-border tw-border-green-500/30">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <p className="tw-text-gray-400 tw-text-sm tw-leading-relaxed">{item.description}</p>
                      </div>
                      <BiRightArrowAlt className="tw-w-5 tw-h-5 tw-text-gray-500 group-hover:tw-text-blue-400 tw-transition-colors tw-duration-200" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Blog Section */}
              <div className="tw-space-y-6">
                <h3 className="tw-text-gray-100 tw-font-bold tw-text-lg">{t('SiteNavbar/l-16')}</h3>
                <div className="tw-space-y-4">
                  {feature.blogs.map((item, index) => (
                    <Link
                      passHref
                      href={item.link}
                      key={index}
                      className="tw-group tw-flex tw-gap-4 tw-p-4 tw-rounded-xl tw-border tw-border-gray-700 hover:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-bg-gray-700"
                      onClick={onClose}
                    >
                      <Image
                        src={item.imageUrl}
                        alt={item.title}
                        width={120}
                        height={80}
                        className="tw-rounded-lg tw-object-cover tw-flex-shrink-0"
                      />
                      <div className="tw-flex-grow tw-min-w-0">
                        <h4 className="tw-text-gray-100 tw-font-semibold tw-mb-2 tw-line-clamp-2 group-hover:tw-text-blue-400 tw-transition-colors tw-duration-200">
                          {item.title}
                        </h4>
                        <p className="tw-text-gray-400 tw-text-sm tw-line-clamp-2 tw-leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Business Partner Dropdown */}
      {showBusinessPartner && (
        <div className="tw-absolute tw-top-full tw-left-0 tw-w-full tw-bg-gray-800 tw-shadow-2xl tw-border-t tw-border-gray-700 tw-z-50 tw-max-h-[90vh] tw-overflow-auto">
          <div className="tw-container tw-mx-auto tw-px-6 tw-py-8 tw-max-h-[80vh] ">
            <div className="tw-max-w-2xl">
              <h3 className="tw-text-gray-100 tw-font-bold tw-text-lg tw-mb-6">{t('SiteNavbar/l-15')}</h3>
              <div className="tw-space-y-4">
                {businessPartner.menu.map((item, index) => (
                  <Link
                    passHref
                    href={item.link}
                    key={index}
                    className="tw-group tw-flex tw-items-start tw-gap-4 tw-p-4 tw-rounded-xl tw-border tw-border-gray-700 hover:tw-border-blue-500 tw-transition-all tw-duration-200 hover:tw-bg-gray-700"
                    onClick={onClose}
                  >
                    <div className="tw-flex-shrink-0 tw-p-2 tw-bg-gray-700 tw-rounded-lg group-hover:tw-bg-gray-600 tw-transition-colors tw-duration-200">
                      {item.icon}
                    </div>
                    <div className="tw-flex-grow">
                      <h4 className="tw-text-gray-100 tw-font-semibold tw-mb-1 group-hover:tw-text-blue-400 tw-transition-colors tw-duration-200">
                        {item.title}
                      </h4>
                      <p className="tw-text-gray-400 tw-text-sm tw-leading-relaxed">{item.description}</p>
                    </div>
                    <BiRightArrowAlt className="tw-w-5 tw-h-5 tw-text-gray-500 group-hover:tw-text-blue-400 tw-transition-colors tw-duration-200" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MobileMenu.displayName = 'MobileMenu';
DesktopMenu.displayName = 'DesktopMenu';
